import { useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { useFileExplorerContext } from './useFileExplorerContext';
import { getFiles, GetFilesResponse } from '../components/FileExplorer/services';
import { folderKeys } from '../components/FileExplorer/utils/queryKeys';

/**
 * Hook to fetch the currently active folder's data from React Query cache.
 * Uses the activeFolderId from FileExplorerContext to determine which folder to fetch.
 *
 * This hook is the single source of truth for active folder data, eliminating
 * the need to duplicate folder content in context state.
 *
 * @returns React Query result with folder data, loading states, and error handling
 * @example
 * ```tsx
 * const { data, isLoading, isError } = useActiveFolderData();
 * const files = data?.data.files;
 * const folders = data?.data.folders;
 * ```
 */
export function useActiveFolderData() {
  const { activeFolder } = useFileExplorerContext();

  return useQuery<AxiosResponse<GetFilesResponse>, Error>({
    queryKey: folderKeys.detail(activeFolder?.id ?? undefined),
    queryFn: () => getFiles({ id: activeFolder!.id }),
    enabled: activeFolder !== null,
  });
}
